import { useEffect, useState, useCallback } from "react";
import _ from "lodash";
import Modal from "../../layout/Modal";
import TextInput from "../../layout/TextInput";
import IntegerInput from "../../layout/IntegerInput";
import Select from "../../layout/Select";
import MultiSelect from "../../layout/MultiSelect";
import Button from "../../layout/Button";
import useFormValidation from "../../../hooks/formValidation";
import { useAuth } from "../../../services/firebase/auth";
import {
  getExtensions,
  IvrMenuOption,
  Queue,
  QueueStrategy,
  Participant,
} from "../../../model/queue.model";
import { isDigitAvailable } from "../../../model/ivrMenu.utils";

function MenuOptionModal({
  shown,
  mode,
  option,
  initialDigit,
  existingOptions,
  queues,
  onClose,
  onSave,
}: MenuOptionModalProps) {
  const [digit, setDigit] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState<"queue" | "submenu">("queue");
  const [queueSource, setQueueSource] = useState<"existing" | "new">(
    "existing"
  );
  const [selectedQueueId, setSelectedQueueId] = useState("");
  const [queueOption, setQueueOption] = useState("");
  const [strategy, setStrategy] = useState<QueueStrategy>();
  const [participants, setParticipants] = useState<any[]>([]);
  const [extensions, setExtensions] = useState<any[]>([]);

  const formValidation = useFormValidation();
  const auth = useAuth();

  const currentDigit = option ? option.option : undefined;
  const discardedOptionsCount =
    option?.type === "submenu" ? option.node.options.length : 0;
  const losesSubmenuOptions = discardedOptionsCount > 0 && type === "queue";

  useEffect(() => {
    if (!shown) return;
    setDigit(option ? String(option.option) : String(initialDigit ?? ""));
    setName(option?.name || "");
    setType(option?.type || "queue");
    if (option?.type === "queue") {
      setQueueSource("existing");
      setSelectedQueueId(option.queueId);
    } else {
      setQueueSource("existing");
      setSelectedQueueId("");
    }
    setStrategy(undefined);
    setParticipants([]);
    setQueueOption("");
  }, [shown, option, initialDigit]);

  const loadExtensions = useCallback(async () => {
    const ext = await getExtensions(auth);
    setExtensions(ext);
  }, [auth]);

  useEffect(() => {
    if (shown) loadExtensions();
  }, [shown, loadExtensions]);

  const digitValidation = (v: string) => {
    const num = parseInt(v, 10);
    if (isNaN(num) || num < 0 || num > 9) {
      return { error: true, message: "Tecla de 0 a 9" };
    }
    if (!isDigitAvailable(existingOptions, num, currentDigit)) {
      return { error: true, message: "Já utilizado neste nível" };
    }
    return { error: false };
  };

  const queueOptionValidation = (v: string) => {
    const used = queues.find((q) => q.option?.toString() === v);
    if (used) return { error: true, message: "Já utilizado" };
    return { error: false };
  };

  const onQueuePicked = (queueId: string) => {
    setSelectedQueueId(queueId);
    const picked = queues.find((q) => (q.id || q.tempId) === queueId);
    if (picked) setName(picked.name);
  };

  const onCloseHandler = () => {
    onClose();
  };

  const saveHandler = async () => {
    if (!formValidation.validateForm()) return;
    const digitNum = parseInt(digit, 10);

    if (type === "submenu") {
      onSave({ option: digitNum, name, kind: "submenu" });
      return;
    }

    if (queueSource === "existing") {
      onSave({
        option: digitNum,
        name,
        kind: "existing-queue",
        queueId: selectedQueueId,
      });
      return;
    }

    const newParticipants: Participant[] = participants.map((v) => {
      const el = extensions.find((a) => v.value === a.value);
      return { id: v.value, name: el.name, extensionNumber: el.extensionNumber };
    });

    onSave({
      option: digitNum,
      name,
      kind: "new-queue",
      newQueue: {
        tempId: _.uniqueId("temp"),
        name,
        option: parseInt(queueOption, 10),
        strategy: strategy as QueueStrategy,
        participants: newParticipants,
      },
    });
  };

  return (
    <Modal
      title={mode === "add" ? "Nova opção" : "Editar opção"}
      className="w-[520px]"
      shown={shown}
      onClose={onCloseHandler}
    >
      <div className="grid grid-cols-1 gap-4 mb-6">
        <div className="grid grid-cols-[100px_1fr] gap-4">
          <IntegerInput
            title="Tecla"
            value={digit}
            setter={setDigit}
            formValidation={formValidation}
            validation={digitValidation}
            required
          />
          <TextInput
            title="Nome da opção"
            value={name}
            setter={setName}
            formValidation={formValidation}
            required
          />
        </div>

        <div>
          <div className="text-xs 2xl:text-sm font-bold text-gray-dark mb-2">
            Ao digitar, o cliente...
          </div>
          <div className="flex rounded-10 border-2 border-gray/50 overflow-hidden">
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-bold ${
                type === "queue" ? "bg-green/10 text-green" : "text-gray-dark"
              }`}
              onClick={() => setType("queue")}
            >
              Entra em uma fila
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-bold border-l-2 border-gray/50 ${
                type === "submenu"
                  ? "bg-purple/10 text-purple"
                  : "text-gray-dark"
              }`}
              onClick={() => setType("submenu")}
            >
              Abre outro nível de menu
            </button>
          </div>
        </div>

        {losesSubmenuOptions && (
          <div className="bg-red/10 text-red text-xs font-bold px-3 py-2 rounded-10">
            Este submenu tem {discardedOptionsCount} opção(ões)
            configurada(s). Mudar para fila vai descartá-las.
          </div>
        )}

        {type === "queue" && (
          <>
            <div className="flex rounded-10 border-2 border-gray/50 overflow-hidden">
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-bold ${
                  queueSource === "existing"
                    ? "bg-white-dark text-primary"
                    : "text-gray-dark"
                }`}
                onClick={() => setQueueSource("existing")}
              >
                Usar fila existente
              </button>
              <button
                type="button"
                className={`flex-1 py-2 text-sm font-bold border-l-2 border-gray/50 ${
                  queueSource === "new"
                    ? "bg-white-dark text-primary"
                    : "text-gray-dark"
                }`}
                onClick={() => setQueueSource("new")}
              >
                Criar nova fila
              </button>
            </div>

            {queueSource === "existing" && (
              <Select
                title="Fila"
                className="w-full"
                value={selectedQueueId}
                setter={onQueuePicked}
                //@ts-ignore
                formValidation={formValidation}
                required
              >
                {queues.map((q, i) => (
                  <option value={q.id || q.tempId} key={i}>
                    {q.name}
                  </option>
                ))}
              </Select>
            )}

            {queueSource === "new" && (
              <>
                <IntegerInput
                  title="Identificador interno"
                  className="w-52"
                  tooltipMessageId="queue-internal-id"
                  value={queueOption}
                  setter={setQueueOption}
                  formValidation={formValidation}
                  validation={queueOptionValidation}
                  required
                />
                <Select
                  title="Estratégia de fila"
                  className="w-full"
                  value={strategy}
                  setter={setStrategy}
                  //@ts-ignore
                  formValidation={formValidation}
                  tooltipMessageId="queue-strategy"
                  required
                >
                  <option value="rrmemory">Rotacionado</option>
                  <option value="linear">Linear</option>
                  <option value="ringall">Todos ramais</option>
                </Select>
                <MultiSelect
                  options={extensions}
                  title="Participantes"
                  formValidation={formValidation}
                  value={participants}
                  setter={setParticipants}
                  required
                />
              </>
            )}
          </>
        )}
      </div>
      <div>
        <Button color="purple" className="float-right" onClick={saveHandler}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
}

export default MenuOptionModal;

export type NewQueueDraft = {
  tempId: string;
  name: string;
  option: number;
  strategy: QueueStrategy;
  participants: Participant[];
};

export type MenuOptionModalResult =
  | { option: number; name: string; kind: "submenu" }
  | { option: number; name: string; kind: "existing-queue"; queueId: string }
  | { option: number; name: string; kind: "new-queue"; newQueue: NewQueueDraft };

export type MenuOptionModalProps = {
  shown: boolean;
  mode: "add" | "edit";
  option?: IvrMenuOption;
  initialDigit?: number;
  existingOptions: IvrMenuOption[];
  queues: Queue[];
  onClose: () => void;
  onSave: (result: MenuOptionModalResult) => void;
};
